import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gavel } from "lucide-react";

const TZ = "America/New_York";

// Offset (ms) between UTC and the given time zone at a specific instant.
const tzOffset = (date, timeZone) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = dtf.formatToParts(date).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour === "24" ? 0 : p.hour, p.minute, p.second);
  return asUTC - date.getTime();
};

// Interpret an admin "YYYY-MM-DDTHH:mm" wall-clock string as Eastern Time and
// return the absolute UTC instant (ms) — identical for every viewer worldwide.
const etWallClockToInstant = (str) => {
  const [datePart, timePart = "00:00"] = str.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi);
  const off1 = tzOffset(new Date(utcGuess), TZ);
  let instant = utcGuess - off1;
  const off2 = tzOffset(new Date(instant), TZ); // refine across DST boundaries
  if (off2 !== off1) instant = utcGuess - off2;
  return instant;
};

const parts = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
};

const Cell = ({ v, l }) => (
  <div className="flex flex-col items-center">
    <span className="font-display text-2xl font-extrabold tabular-nums leading-none">{String(v).padStart(2, "0")}</span>
    <span className="mt-0.5 text-[10px] font-bold uppercase tracking-wider opacity-80">{l}</span>
  </div>
);

export const AuctionCountdown = () => {
  const [target, setTarget] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    api.get("/settings").then((r) => {
      const dt = r.data?.auction_datetime;
      if (dt) {
        const t = etWallClockToInstant(dt);
        if (!isNaN(t)) setTarget(t);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!target) return null;
  const diff = target - now;
  const live = diff <= 0;
  const { d, h, m, s } = parts(diff);
  const etLabel = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  }).format(new Date(target)) + " ET";

  return (
    <div data-testid="auction-countdown" className="mx-4 mt-2 rounded-xl bg-primary p-4 text-primary-foreground shadow-sm">
      <p className="flex items-center gap-2 text-sm font-bold">
        <Gavel size={16} /> {live ? "The Auction is Live Now!" : "Auction Starts In"}
      </p>
      {!live && (
        <div className="mt-2 flex items-center gap-5">
          <Cell v={d} l="Days" />
          <Cell v={h} l="Hrs" />
          <Cell v={m} l="Min" />
          <Cell v={s} l="Sec" />
        </div>
      )}
      <p className="mt-2 text-[11px] font-semibold opacity-80" data-testid="auction-countdown-when">{etLabel}</p>
    </div>
  );
};
